import * as React from 'react'
import { VendorProfileLink } from '@/components/VendorProfileLink'
import { Mail, Pencil, Plus, Store } from 'lucide-react'
import {
  useAddVenueVendor,
  useCorrectVendorOwnerEmail,
  useStopVenueVendor,
  useVenueVendorServices,
  useSetVenueVendorServices,
  useVenueVendorRequests,
  useAcceptVenueRequest,
  useDeclineVenueRequest,
} from '@/hooks/useVendors'
import { requestErrorMessage } from '@/helpers/apiClient'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { LoadingState, ErrorState } from '@/components/ui/states'
import { ConfirmDialog } from '@/components/ui/confirm-dialog'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { FormError } from '@/components/ui/form-error'
import { Card } from '@/components/ui/card'
import { UserStatusBadge, inviteActionLabel } from '@/components/UserStatusBadge'
import { VendorCompanyPicker } from '@/components/VendorCompanyPicker'
import { VendorRemovalDialog } from '@/components/VendorRemovalDialog'
import { VENDOR_SERVICES, VENDOR_SERVICE_LABELS, type VendorService } from '@lib/enums/vendor-service.enum'
import type { VendorServiceApprovalDto } from '@lib/dto/vendor.dto'
import { correctVendorOwnerEmailInputSchema } from '@lib/input/vendor.input'

/**
 * Who the venue deals with at a company, and — for a company this venue set up
 * whose owner has not accepted — the way to put a mistyped address right or
 * send the invitation again (decision 27). Another venue's company is not this
 * venue's to redirect.
 */
function VendorContact({ venueId, vendor, canEdit }: { venueId: string; vendor: VendorServiceApprovalDto; canEdit: boolean }) {
  const correct = useCorrectVendorOwnerEmail(venueId)
  const [editing, setEditing] = React.useState(false)
  const [email, setEmail] = React.useState('')
  const [msg, setMsg] = React.useState<string | null>(null)
  const contact = vendor.contact
  if (!contact) return null
  const canCorrect = canEdit && vendor.setUpHere && contact.status === 'PENDING'

  function send(to: string): void {
    const parsed = correctVendorOwnerEmailInputSchema.safeParse({ email: to })
    if (!parsed.success) {
      setMsg(parsed.error.issues[0]?.message ?? 'Enter a valid email.')
      return
    }
    setMsg(null)
    correct.mutate(
      { vendorId: vendor.vendorId, email: parsed.data.email },
      { onSuccess: () => setEditing(false), onError: (e) => setMsg(requestErrorMessage(e)) },
    )
  }

  return (
    <div className="mt-1.5 space-y-2 pl-6">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className="text-xs text-ink-muted">
          {contact.name} · {contact.email}
        </span>
        <UserStatusBadge status={contact.status} invitedAt={contact.invitedAt} inviteExpiresAt={contact.inviteExpiresAt} />
        {canCorrect && !editing ? (
          <span className="flex items-center gap-1">
            <Button variant="ghost" size="sm" disabled={correct.isPending} onClick={() => send(contact.email)}>
              <Mail className="h-4 w-4" /> {inviteActionLabel(contact.invitedAt)}
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => {
                setEmail(contact.email)
                setMsg(null)
                setEditing(true)
              }}
            >
              <Pencil className="h-4 w-4" /> Correct email
            </Button>
          </span>
        ) : null}
      </div>
      {editing ? (
        <div className="flex flex-wrap items-end gap-2">
          <Field label="Owner’s email" htmlFor={`owner-email-${vendor.vendorId}`}>
            <Input
              id={`owner-email-${vendor.vendorId}`}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-72"
            />
          </Field>
          <Button size="sm" disabled={correct.isPending || email.trim() === ''} onClick={() => send(email)}>
            Save and invite
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setEditing(false)}>
            Cancel
          </Button>
        </div>
      ) : null}
      {canCorrect && editing ? (
        <p className="text-xs text-ink-muted">The invitation sent to the old address stops working.</p>
      ) : null}
      {msg ? <FormError size="sm">{msg}</FormError> : null}
    </div>
  )
}

/**
 * Set a company up at the venue for one service: one already on the platform,
 * or a new one and its owner. What it is here for is chosen first, because a
 * company can be added only for something it offers.
 */
function AddVendor({ venueId, vendors }: { venueId: string; vendors: VendorServiceApprovalDto[] }) {
  const add = useAddVenueVendor(venueId)
  const [open, setOpen] = React.useState(false)
  const [service, setService] = React.useState<VendorService>('planning')

  if (!open) {
    return (
      <Button variant="outline" size="sm" onClick={() => setOpen(true)}>
        <Plus className="h-4 w-4" /> Add a vendor
      </Button>
    )
  }

  const approved = new Set(vendors.filter((v) => v.approved.includes(service)).map((v) => v.vendorId))
  return (
    <Card className="space-y-3 p-4">
      <div className="flex items-end justify-between gap-3">
        <Field label="For" htmlFor="add-vendor-service">
          <Select
            id="add-vendor-service"
            value={service}
            onChange={(e) => {
              const next = VENDOR_SERVICES.find((s) => s === e.target.value)
              if (next) setService(next)
            }}
          >
            {VENDOR_SERVICES.map((s) => (
              <option key={s} value={s}>
                {VENDOR_SERVICE_LABELS[s]}
              </option>
            ))}
          </Select>
        </Field>
        <Button variant="ghost" size="sm" onClick={() => setOpen(false)}>
          Done
        </Button>
      </div>
      <VendorCompanyPicker
        source={{ kind: 'venue', venueId }}
        service={service}
        idPrefix="add-vendor"
        isAdded={(vendorId) => approved.has(vendorId)}
        onChoose={async (input) => {
          await add.mutateAsync(input)
        }}
      />
    </Card>
  )
}

/** What a vendor is approved for here, and what else it offers, for someone who can't change either. */
function ApprovedServices({ vendor }: { vendor: VendorServiceApprovalDto }) {
  const approved = vendor.offered.filter((s) => vendor.approved.includes(s))
  const others = vendor.offered.filter((s) => !vendor.approved.includes(s))
  return (
    <div className="mt-1 space-y-0.5 pl-6 text-xs text-ink-muted">
      <p>Approved here: {approved.length === 0 ? 'nothing yet' : approved.map((s) => VENDOR_SERVICE_LABELS[s]).join(', ')}</p>
      {others.length > 0 ? <p>Also offers: {others.map((s) => VENDOR_SERVICE_LABELS[s]).join(', ')}</p> : null}
    </div>
  )
}

/** One associated vendor: its offered services as approve/revoke checkboxes,
 * saved immediately on toggle (replace-set). */
function VendorRow({
  venueId,
  venueName,
  vendor,
  busy,
  onToggle,
  canEdit,
}: {
  venueId: string
  venueName: string
  vendor: VendorServiceApprovalDto
  busy: boolean
  onToggle: (vendor: VendorServiceApprovalDto, service: VendorService) => void
  canEdit: boolean
}) {
  const approved = new Set(vendor.approved)
  const stop = useStopVenueVendor(venueId)
  const [stopping, setStopping] = React.useState(false)
  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-2">
        <Store className="h-4 w-4 shrink-0 text-ink-muted" />
        <span className="min-w-0 flex-1 font-medium">
          <VendorProfileLink
            vendorId={vendor.vendorId}
            name={vendor.vendorName}
            context={{ venue: { id: venueId, name: venueName, worksWith: true, recommended: vendor.recommended } }}
          />
          {vendor.recommended ? (
            <Badge tone="sage" className="ml-2 align-middle">
              Recommended
            </Badge>
          ) : null}
        </span>
        {canEdit ? (
          <Button
            variant="ghost"
            size="sm"
            className="text-danger-ink hover:text-danger-ink"
            onClick={() => {
              stop.reset()
              setStopping(true)
            }}
          >
            Stop working with
          </Button>
        ) : null}
      </div>
      <VendorRemovalDialog
        open={stopping}
        onOpenChange={setStopping}
        target={{ kind: 'venue', venueId, vendorId: vendor.vendorId }}
        title={`Stop working with ${vendor.vendorName}?`}
        lead="It will no longer be one of this venue’s vendors, and nothing it offers will be available here."
        confirmLabel="Stop working with them"
        busy={stop.isPending}
        error={stop.error ? requestErrorMessage(stop.error) : null}
        onConfirm={() => stop.mutate({ vendorId: vendor.vendorId }, { onSuccess: () => setStopping(false) })}
      />
      <VendorContact venueId={venueId} vendor={vendor} canEdit={canEdit} />
      {vendor.offered.length > 0 && !canEdit ? <ApprovedServices vendor={vendor} /> : null}
      {vendor.offered.length === 0 ? (
        <p className="mt-1 pl-6 text-xs text-ink-muted">This vendor doesn&rsquo;t offer any services yet.</p>
      ) : canEdit && (
        <ul className="mt-2 flex flex-wrap gap-x-6 gap-y-2 pl-6">
          {vendor.offered.map((service) => (
            <li key={service}>
              <label className="flex cursor-pointer items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={approved.has(service)}
                  disabled={busy}
                  onChange={() => onToggle(vendor, service)}
                />
                {VENDOR_SERVICE_LABELS[service]}
              </label>
            </li>
          ))}
        </ul>
      )}
    </li>
  )
}

/**
 * The venue's per-service approvals for the vendors a super admin has associated
 * with it. Approving a service makes that vendor's offering (e.g. furniture)
 * available in this venue's events.
 */
/** Vendors that have requested to serve this venue — accept (→ active, then
 * approvable) or decline (→ removed). Renders nothing when there are none. */
function PendingRequests({ venueId, venueName, canEdit }: { venueId: string; venueName: string; canEdit: boolean }) {
  const query = useVenueVendorRequests(venueId)
  const accept = useAcceptVenueRequest(venueId)
  const decline = useDeclineVenueRequest(venueId)
  const busy = accept.isPending || decline.isPending
  // Its own line: the service approvals' errors further down are not about these two answers.
  const [failure, setFailure] = React.useState<string | null>(null)

  if (!query.data || query.data.requests.length === 0) return null

  function answer(vendorId: string, reply: typeof accept | typeof decline): void {
    setFailure(null)
    reply.mutate({ vendorId }, { onError: (e) => setFailure(requestErrorMessage(e)) })
  }

  return (
    <div className="space-y-2">
      <h4 className="text-sm font-medium text-ink">Requests to serve</h4>
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {query.data.requests.map((r) => (
          <li key={r.vendorId} className="flex items-center gap-3 px-4 py-3">
            <Store className="h-4 w-4 shrink-0 text-ink-muted" />
            <span className="min-w-0 flex-1 truncate text-sm font-medium">
              {/* Asking, not serving: nothing to recommend yet. */}
              <VendorProfileLink
                vendorId={r.vendorId}
                name={r.vendorName}
                context={{ venue: { id: venueId, name: venueName, worksWith: false, recommended: false } }}
              />
            </span>
            {canEdit ? (
              <div className="flex items-center gap-1.5">
                <Button size="sm" disabled={busy} onClick={() => answer(r.vendorId, accept)}>
                  Accept
                </Button>
                <Button
                  size="sm"
                  variant="ghost"
                  className="text-danger-ink hover:text-danger-ink"
                  disabled={busy}
                  onClick={() => answer(r.vendorId, decline)}
                >
                  Decline
                </Button>
              </div>
            ) : null}
          </li>
        ))}
      </ul>
      {failure ? <FormError size="sm">{failure}</FormError> : null}
    </div>
  )
}

export function VenueVendorApprovals({
  venueId,
  venueName,
  canEdit,
}: {
  venueId: string
  /** What the venue's recommendations are given as: "Recommended by {venueName}". */
  venueName: string
  /** Edit adds adding vendors, answering their requests, approving their services, and stopping work with one. */
  canEdit: boolean
}) {
  const query = useVenueVendorServices(venueId)
  const setApprovals = useSetVenueVendorServices(venueId)
  /** A furniture approval about to be withdrawn from a vendor that events here rent from. */
  const [revoking, setRevoking] = React.useState<{ vendor: VendorServiceApprovalDto; next: VendorService[] } | null>(
    null,
  )

  function toggle(vendor: VendorServiceApprovalDto, service: VendorService): void {
    const approved = new Set(vendor.approved)
    if (approved.has(service)) approved.delete(service)
    else approved.add(service)
    // Keep the approved set ordered by, and confined to, what the vendor offers.
    const next = vendor.offered.filter((s) => approved.has(s))
    // Taking furniture away from a vendor upcoming events rent from: say so first.
    if (service === 'furniture' && !approved.has('furniture') && vendor.upcomingRentalEvents > 0) {
      setRevoking({ vendor, next })
      return
    }
    setApprovals.mutate({ vendorId: vendor.vendorId, services: next })
  }

  function renderApprovals() {
    if (query.isPending) {
      return (
        <LoadingState label="Loading vendors…" />
      )
    }
    if (query.isError) {
      return (
        <ErrorState message={requestErrorMessage(query.error)} onRetry={() => void query.refetch()} />
      )
    }
    if (query.data.vendors.length === 0) {
      return (
        <p className="py-8 text-center text-sm text-ink-muted">
          {canEdit
            ? 'No vendors work with this venue yet. Add one, or accept a request above; then you approve which of its services are available here.'
            : 'No vendors work with this venue yet.'}
        </p>
      )
    }
    return (
      <ul className="divide-y divide-line overflow-hidden rounded-md border border-line">
        {query.data.vendors.map((vendor) => (
          <VendorRow
            key={vendor.vendorId}
            venueId={venueId}
            venueName={venueName}
            vendor={vendor}
            busy={setApprovals.isPending}
            onToggle={toggle}
            canEdit={canEdit}
          />
        ))}
      </ul>
    )
  }

  return (
    <>
    <div className="space-y-5">
      <PendingRequests venueId={venueId} venueName={venueName} canEdit={canEdit} />
      {canEdit ? <AddVendor venueId={venueId} vendors={query.data?.vendors ?? []} /> : null}
      {renderApprovals()}
      {setApprovals.isError ? <FormError>{requestErrorMessage(setApprovals.error)}</FormError> : null}
    </div>
      <ConfirmDialog
        open={revoking !== null}
        onOpenChange={(open) => {
          if (!open) setRevoking(null)
        }}
        title={`Stop approving ${revoking?.vendor.vendorName ?? 'this vendor'} for furniture?`}
        description={
          revoking
            ? `${String(revoking.vendor.upcomingRentalEvents)} upcoming ${revoking.vendor.upcomingRentalEvents === 1 ? 'event here rents' : 'events here rent'} their furniture. They keep what they have rented; nothing new can be rented from ${revoking.vendor.vendorName} here.`
            : ''
        }
        confirmLabel="Stop approving"
        destructive
        busy={setApprovals.isPending}
        error={setApprovals.isError ? requestErrorMessage(setApprovals.error) : null}
        onConfirm={() => {
          if (!revoking) return
          // The set captured when the dialog opened is stale once another
          // service was approved while it sat there. Read the list as it is now.
          const current = query.data?.vendors.find((vendor) => vendor.vendorId === revoking.vendor.vendorId)
          const approved = new Set(current?.approved ?? revoking.next)
          approved.delete('furniture')
          const offered = current?.offered ?? revoking.vendor.offered
          const services = offered.filter((service) => approved.has(service))
          setApprovals.mutate(
            { vendorId: revoking.vendor.vendorId, services },
            { onSuccess: () => setRevoking(null) },
          )
        }}
      />
    </>

  )
}
